/**
 * Controlled halts: EHQ.abortJob / requestRetry / requestReview end a step on
 * purpose. RoutineService flags the halt on globalThis before throwing (a thrown
 * class does not survive the Pyodide or QuickJS boundary), and the step runtimes
 * read the flag after the step, as enoch does. A halt is not a failure, but it
 * does end the run: enoch finishes the job as Aborted, Retry or NeedsReview.
 */
export type ControlHaltKind = 'aborted' | 'retry' | 'review';

export interface ControlHalt {
  kind: ControlHaltKind;
  message: string;
}

const HALT_SIGNALS: Array<{ kind: ControlHaltKind; key: string }> = [
  { kind: 'aborted', key: '_abortRequested' },
  { kind: 'retry', key: '_retryRequested' },
  { kind: 'review', key: '_reviewRequested' },
];

/** Label shown in the step output for each halt kind. */
export const CONTROL_HALT_LABELS: Record<ControlHaltKind, string> = {
  aborted: 'Job aborted',
  retry: 'Retry requested',
  review: 'Review requested',
};

/**
 * Clear any halt flags before a step runs. The browser globalThis outlives a
 * step (unlike enoch's per-step process), so a flag left by an earlier step
 * would otherwise mis-render this one.
 */
export function resetControlHalt(): void {
  const g = globalThis as Record<string, unknown>;
  for (const { key } of HALT_SIGNALS) {
    g[key] = undefined;
  }
}

/**
 * Read and clear the halt signalled during the step, if any. Honoured even when
 * step code swallowed the thrown exception, matching enoch's post-step check.
 */
export function consumeControlHalt(): ControlHalt | null {
  const g = globalThis as Record<string, unknown>;
  for (const { kind, key } of HALT_SIGNALS) {
    const message = g[key];
    if (message) {
      g[key] = undefined;
      return { kind, message: String(message) };
    }
  }
  return null;
}
